import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';
import '../../assets/css/analyzeResults.scss';
import '../../assets/css/resultDetailsModal.scss';
import '../../assets/css/highestScore.scss';
import '../../assets/css/lazyImage.scss';
import Loader from './Loader';
import ResultDetailsModal from './ResultDetailsModal';
import Details from '../Modals/Details';
import LazyImage from './LazyImage';
import "../../config";
import api from '../../utils/api';

import Utilities from '../../assets/js/Utilities';

const AnalyzeProductResults = ({ results, onResultSelect, probeItem, analysisStat }) => {
  const { language } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);

  // States
  const [productData, setProductData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('Make'); // 'Make', 'Model', or 'ShoeType'
  const [filterText, setFilterText] = useState(''); 
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Details modal states
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchProductData = useCallback(async () => {
      if (!results || results.length === 0) return; 
      setIsLoading(true);
      setError(null);

      try {
        console.log("Fetching product data for results:", results);

        // Extract IDValues from results
        const idValues = results.map(result => result.ProductID);

        // Make API request to get full metadata
        const response = await api.post(`${global.config.api.domain}/fwiq-api/api/catalog/search`, {
          PageNumber: 1,
          PageSize: idValues.length,
          IDs: idValues
        });

        // const dataText = await response.text();

        const data = response.data;
        // Check the structure of the results
        // console.log("API results:", results);

        // Merge the score from results with the full product data
        const mergedData = data.Records.map(product => {
          const matchingResult = results.find(r => (r.ProductID === product.IDValue));
          // console.log("Matching result for product", product.IDValue, ":", matchingResult);

          product.score = matchingResult ? matchingResult.Scores[0]?.Score || 0 : 0; // Use the first score if available

          return {
            ...product,
            Scores: matchingResult ? matchingResult.Scores : [],
            score: matchingResult ? matchingResult.Scores[0]?.Score || 0 : 0, // Use the first score if available
            imageScores: matchingResult ? matchingResult.Scores || [] : [] // Individual image scores
          };
        });

        // Sort by score (highest first)
        mergedData.sort((a, b) => b.score - a.score);

        setProductData(mergedData);
      } catch (err) {
        console.error('Error fetching product data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }, [results]);

  // Fetch full product data using IDValues from results
  useEffect(() => {  
    fetchProductData();
  }, [fetchProductData]);

  // Filter the products based on active filter and filter text
  const filteredProducts = useMemo(() => {
    if (!filterText.trim()) return productData;

    const searchTerm = filterText.toLowerCase().trim();

    return productData.filter(product => {
      switch (activeFilter) {
        case 'Make':
          return product.Make?.toLowerCase().includes(searchTerm);
        case 'Model':
          return product.Model?.toLowerCase().includes(searchTerm);
        case 'ShoeType':
          return product.ShoeType?.toLowerCase().includes(searchTerm);
        default:
          return true;
      }
    });
  }, [productData, activeFilter, filterText]);

  // Get unique values for each filter type
  const uniqueValues = useMemo(() => {
    const makes = new Set();
    const models = new Set();
    const shoeTypes = new Set();

    productData.forEach(product => {
      if (product.Make) makes.add(product.Make);
      if (product.Model) models.add(product.Model);
      if (product.ShoeType) shoeTypes.add(product.ShoeType);
    });

    return {
      Make: Array.from(makes).sort(),
      Model: Array.from(models).sort(),
      ShoeType: Array.from(shoeTypes).sort()
    };
  }, [productData]);
  const getPreviewImage = (product) => {
    // If we don't have image scores or images, return an empty string
    if (!product.imageScores || !Array.isArray(product.imageScores) || !product.Images || !Array.isArray(product.Images) || product.Images.length === 0) {
      return "";
    }

    // Find the image with the highest score
    let highestScore = -1;
    let highestScoreImageId = null;

    // First find the highest scoring image ID
    product.imageScores.forEach(scoreEntry => {
      if (scoreEntry.Score > highestScore) {
        highestScore = scoreEntry.Score;
        highestScoreImageId = scoreEntry.ImageID;
      }
    });

    // Now find that image in the Images array
    const imageInfo = product.Images.find(img => img.IDValue === highestScoreImageId);

    // If no matching image was found or it has no highest score, fall back to the original sole image logic
    if (!imageInfo || !imageInfo.S3RelativePath) {
      // Fallback to the original logic - find any sole image
      const fallbackImageInfo = product.Images.find(img =>
        img.Type === global.config.Defines.ImageTypeKey.SHOE_SOLE_LEFT ||
        img.Type === global.config.Defines.ImageTypeKey.SHOE_SOLE_RIGHT ||
        img.Type === global.config.Defines.ImageTypeKey.SHOE_SOLE_UNDETERMINED
      );

      // If even the fallback didn't find an image, return an empty string
      if (!fallbackImageInfo || !fallbackImageInfo.S3RelativePath) return "";

      // Otherwise use the fallback image
      let s3Path = fallbackImageInfo.S3RelativePath.startsWith("/")
        ? fallbackImageInfo.S3RelativePath.substring(1)
        : fallbackImageInfo.S3RelativePath;
      let _linkImgS3 = global.config.images3.link;
      if (!_linkImgS3) return "";

      return _linkImgS3.endsWith("/")
        ? `${_linkImgS3}${s3Path}`
        : `${_linkImgS3}/${s3Path}`;
    }

    // Use the highest scoring image
    let s3Path = imageInfo.S3RelativePath.startsWith("/")
      ? imageInfo.S3RelativePath.substring(1)
      : imageInfo.S3RelativePath;
    let _linkImgS3 = global.config.images3.link;
    if (!_linkImgS3) return "";

    return _linkImgS3.endsWith("/")
      ? `${_linkImgS3}${s3Path}`
      : `${_linkImgS3}/${s3Path}`;
  }

  // Handle filter selection
  const handleFilterChange = (type) => {
    setActiveFilter(type);
    setFilterText('');
  };
  // Handle view details
  const handleViewDetails = (product) => {
    product.Category_ID = 1;
    // Set the selected record for Details modal
    setSelectedRecord(product);

    // console.log("Opening Details modal for product:", productData);
    
    // Open Details modal using dialog like Retail.jsx
    if (dialogRef.current && dialogRef.current.showModal) {
      dialogRef.current.showModal();
    }
  };

  // Handle Details modal close
  const handleCloseDetails = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
    }
    setSelectedRecord(null);
  };// Handle card click to show modal
  const handleCardClick = (product) => {    // Ensure we have all the necessary data for the modal
    // setSelectedProduct(product);
    // setIsModalOpen(true);

    handleViewDetails(product);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // handle get rank
  const handleGetRank = (product) => {
    if (!product || !product.IDValue) return 0;
    let rank = productData.findIndex(p => p.IDValue === product.IDValue);
    return rank >= 0 ? rank + 1 : 0; // Convert to 1-based index
  }

  // Helper function to get score class
  const getScoreClass = (product) => {

    if (product.score >= 25) return 'excellent';
    if (product.score >= 17.5) return 'good';
    if (product.score >= 12.5) return 'moderate';
    return 'low';
  };
  return (
    <div className="analyze-results">
      {isLoading ? (
        <div className="loading-container">
          <Loader />
          <p>{language?.analyze?.loadingResults || 'Loading results...'}</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <i className="fa fa-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      ) : (
        <>
          {/* Filter Section */}
          <div className="results-filter">
            <div className="filter-tabs">
              <button
                className={`filter-tab ${activeFilter === 'Make' ? 'active' : ''}`}
                onClick={() => handleFilterChange('Make')}
              >
                <i className="fa fa-industry"></i>
                {language?.analyze?.make || 'Make'}
              </button>
              <button
                className={`filter-tab ${activeFilter === 'Model' ? 'active' : ''}`}
                onClick={() => handleFilterChange('Model')}
              >
                <i className="fa fa-tag"></i>
                {language?.analyze?.model || 'Model'}
              </button>
              <button
                className={`filter-tab ${activeFilter === 'ShoeType' ? 'active' : ''}`}
                onClick={() => handleFilterChange('ShoeType')}
              >
                <i className="fa fa-shoe-prints"></i>
                {language?.analyze?.shoeType || 'Shoe Type'}
              </button>
            </div>

            <div className="filter-input-container">
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={`${language?.analyze?.filterBy || 'Filter by'} ${activeFilter}...`}
                className="filter-input"
              />

              {filterText && (
                <button className="clear-filter" onClick={() => setFilterText('')}>
                  <i className="fa fa-times"></i>
                </button>
              )}
            </div>

            {/* Quick filter pills */}
            <div className="filter-pills">
              {uniqueValues[activeFilter]?.slice(0, 10).map(value => (
                <button
                  key={value}
                  className="filter-pill"
                  onClick={() => setFilterText(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <div className="results-count">
            {analysisStat && (
              <div className="analysis-stats">

                <span><strong>{Utilities.FormatNumber(results.length) || 0}</strong> {language?.analyze?.resultFound || 'Result found'} </span>
                &nbsp; - &nbsp;
                <span>{language?.analyze?.searchTime || 'Search Time'}: <strong>{Utilities.FormatNumber(analysisStat.TotalTimeMs)}</strong> ms</span>
              </div>
            )}
          </div>

          {/* Results Grid */}
          <div className="results-grid">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <div
                  className="result-card"
                  key={product.IDValue || index}
                  onClick={() => handleCardClick(product)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="result-header">
                    <div className="result-rank">
                      <span> {language?.analyze?.rank || "Rank"}: {handleGetRank(product)}</span>
                    </div>
                    <span className={`result-score ${getScoreClass(product)}`}>
                      {/* <span> {language?.analyze?.rank || "Rank"}: {handleGetRank(product)}</span> */}
                      <span> {language?.analyze?.score || "Score"}: {product.score.toFixed(2)}</span>
                    </span>
                  </div>
                  <div className="result-image">
                    <LazyImage
                      src={getPreviewImage(product)}
                      className="product-image"
                      alt={`${product.Make} ${product.Model}`}
                    />
                    {/* {product.imageScores && Array.isArray(product.imageScores) && product.imageScores.length > 0 && (
                      <span className="best-match-badge">
                        <i className="fa fa-star"></i> Best Match
                      </span>
                    )} */}
                  </div>

                  <div className="result-details">
                    <div className="detail-row">
                      <span className="detail-label">{language?.analyze?.make || 'Make'}:</span>
                      <span className="detail-value highlight">{product.Make || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{language?.analyze?.shoeType || 'Shoe Type'}:</span>
                      <span className="detail-value">{product.ShoeType || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{language?.analyze?.model || 'Model'}:</span>
                      <span className="detail-value">{product.Model || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{language?.analyze?.productCode || 'Product Code'}:</span>
                      <span className="detail-value">{product.ProductCode || 'N/A'}</span>
                    </div>
                    <div className="action-buttons">
                      <button
                        className="view-details-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click when button is clicked
                          handleViewDetails(product);
                        }}
                      >
                        <i className="fa fa-info-circle"></i>
                        {language?.analyze?.details || 'Details'}
                      </button>                      <button
                        className="compare-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click when button is clicked
                          if (onResultSelect) {
                            // Pass a second parameter to indicate this is a compare action
                            onResultSelect(product, 'compare');
                          }
                        }}
                      >
                        <i className="fa fa-exchange"></i>
                        {language?.analyze?.compare || 'Compare'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <i className="fa fa-search"></i>
                <p>{language?.analyze?.noResultsFound || 'No results match your filter.'}</p>
                
              </div>)}
          </div>
        </>
      )}      
      {/* Details Modal */}
      <ResultDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        productDetails={selectedProduct}
      />
      
      {/* Details Dialog - Same approach as Retail.jsx */}
      <dialog ref={dialogRef} className="dialog">
        {selectedRecord && (
          <Details
            record={selectedRecord}
            onClose={handleCloseDetails}
            recordScore={productData.Scores}
          />
        )}
      </dialog>
    </div>
  );
};

export default AnalyzeProductResults;